/*
* BuroEcl
* Copyright (c) 2023 Buroclic. All rights reserved.
* 
* This file contains usefull functions that can be called from anywhere
* specifically allowing to format data, date etc.
*/

import { Chemise, Dossier, UIChemise } from "../../interfaces/IDossiers";


/**
 * Get every chemises to display in the TreeView
 * Based on a recordset that contains every chemises which got
 * published element into it
 * We do that by taking their PAR_CHEMIN and PAR_REF_CHEMIN
 * Which is their 'chemises' PATH as it would be in Windows
 * @author Mickaël "Naxe67" APPEL
 * @param {Array<Chemise>} chemisesWithEl 
 * @returns {Array<UIChemise>}
 * @since 2023-09-19
 * @deprecated
 */
export const getTreeViewChemises = (chemisesWithEl: Array<Chemise>): Array<UIChemise> => {
    try {
        const treeviewChemises: Array<UIChemise> = [];

        // Browse each chemises which got published element into it
        // in order to create their potential parent chemise if needed
        chemisesWithEl.forEach((chemiseWithEl) => {
            const parentParNom = chemiseWithEl.PAR_CHEMIN.match(/[^\\\\\r\n]+/gmi);
            const parentParRef = chemiseWithEl.PAR_REF_CHEMIN.match(/[^\\\\\r\n]+/gmi);

            const length = parentParNom ? parentParNom.length : 0;

            if (parentParNom && parentParRef) {
                let path = '\\\\'; // starts with 2 \
                let nomPath = '\\\\';

                // Exluding the chemise which contains the actual published elements (lowest layer)
                if (parentParNom.length > 1 && parentParRef.length > 1 && parentParNom.length === parentParRef.length) {

                    // For each parent
                    for (let i = 0; i < length - 1; i++) {
                        const parRef = Number(parentParRef[i])
                        path += parentParRef[i] + '\\\\';
                        nomPath += parentParNom[i] + '\\\\';

                        // Add the parent in the treeview if it has not been added yet
                        if (treeviewChemises.filter((chemise) => { return chemise.PAR_REF === parRef }).length === 0) {
                            // Add it in the treeview
                            treeviewChemises.push({
                                PAR_NOM: parentParNom[i],
                                PAR_REF: parRef,
                                MIS_REF: chemiseWithEl.MIS_REF,
                                PAR_REFPERE: (i > 0 ? Number(parentParRef[i - 1]) : 0),
                                PAR_REF_CHEMIN: path,
                                PAR_NOM_CHEMIN: nomPath
                            })
                        }
                    }
                }

                // Add the actual chemise
                if (treeviewChemises.filter((chemise) => { return chemise.PAR_REF === chemiseWithEl.PAR_REF }).length === 0) {
                    treeviewChemises.push({
                        PAR_NOM: chemiseWithEl.PAR_NOM,
                        PAR_REF: chemiseWithEl.PAR_REF,
                        MIS_REF: chemiseWithEl.MIS_REF,
                        PAR_REFPERE: chemiseWithEl.PAR_REFPERE,
                        PAR_REF_CHEMIN: path + chemiseWithEl.PAR_REF + '\\\\',
                        PAR_NOM_CHEMIN: nomPath + chemiseWithEl.PAR_NOM + '\\\\'
                    });
                }
            }

        });

        return treeviewChemises;
    } catch (err) {
        throw new Error(`Error while creating visual parents elements : ${err}`);
    }
}



/**
 * Get a file extension based on its filename
 * @author Mickaël "Naxe67" APPEL
 * @param {string} docFile 
 * @returns {string} Extension
 * @since 2023-09-21
 */
export const getExtFromFilename = (docFile: string): string => {
    const extResult = /(?:\.([^.]+))?$/.exec(docFile);
    const ext = (extResult) ? (extResult.length >= 1) ? extResult[1]?.toUpperCase() : '' : '';

    return ext;
}



/**
 * Get the icon image representing the extension of a filename given in param
 * @author Mickaël "Naxe67" APPEL
 * @param {string} docFile 
 * @returns {React.JSX.Element}
 * @since 2023-09-20
 */
export const getTypeImg = (docFile: string, isAlreadyExt: boolean = false): React.JSX.Element => {

    // get document extension
    const ext = (isAlreadyExt) ? docFile : getExtFromFilename(docFile);
    let extImg = '';

    switch (ext) {
        // PDF
        case 'PDF':
            extImg = 'pdf.png'; break;

        // IMG
        case 'BMP':
        case 'JPEG':
        case 'JPG':
        case 'PNG':
        case 'GIF':
        case 'TIF':
        case 'SVG':
            extImg = 'image.png'; break;

        // EXCEL
        case 'XLSX':
        case 'XLSM':
        case 'XLSB':
        case 'XLS':
        case 'XLTM':
        case 'XLT':
        case 'ODS':
        case 'CSV':
            extImg = 'excel.png'; break;

        // WORD
        default:
            extImg = 'word.png'; break;
    }

    return <img src={`/images/${extImg}`} alt={ext} title={ext} className="w-8 h-8" />;

}



/**
 * Format a Date (as string) to DD/MM/YYYY
 * @author Mickaël "Naxe67" APPEL
 * @param {string} strDate 
 * @returns {string}
 * @since 2023-09-20
 */
export const dateToDDMMYYYY = (strDate: string, withTime: boolean = false): string => {
    const date: Date = new Date(strDate);
    const month: number = date.getMonth() + 1;
    const day: number = date.getDate();
    const year: number = date.getFullYear();

    // Add leading zeros if needed
    const formatedMonth: string = month.toString().padStart(2, '0');
    const formatedDay: string = day.toString().padStart(2, '0');

    const formatedDate: string = `${formatedDay}/${formatedMonth}/${year}`

    // Handle time if needed
    if (withTime) {
        const formatedHour = date.getHours().toString().padStart(2, '0');
        const formatedMinute = date.getMinutes().toString().padStart(2, '0');
        return formatedDate + ` ${formatedHour}:${formatedMinute}`;
    } else
        return formatedDate;
}



/**
 * Format a dd/MM/YYYY string to DD/MM/YYYY
 * @author Mickaël "Naxe67" APPEL
 * @param {string} strDate 
 * @returns {string}
 * @since 2024-04-09
 */
export const DDMMYYYYToDate = (strDate: string): string => {
    const parts = strDate.split('/');

    if (parts.length === 3) {
        return `${parts[1]}/${parts[0]}/${parts[2]}`;
    }

    return strDate; // failed
}



/**
 * Escape a string in order to match the whole string litteraly in a regex
 * @author Mickaël "Naxe67" APPEL
 * @param {string} pattern
 * @returns {string}
 * @since 2023-09-21
 */
export const escapeRegExp = (pattern: string): string => {
    return pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}



/**
 * Returns true if the given string match the minimum expectations for a password definition
 * Returns false otherwise
 * @author Mickaël "Naxe67" APPEL
 * @param {string} str 
 * @returns {boolean}
 */
export const isAValidPassword = (str: string) => {
    // empty string
    if (!str) return false;

    // Proceed to the expectations check using RegExp
    //     (1 upperca.)(1 lowerca.) (1 number) (1 special charact.)(min. 8 char)
    if (!/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{8,}$/.test(str)) return false;
    //"'()+,./:;<=>[\]_`{|}~ // TO ADD
    //(?=.*?[#?!@$ %^&*\-"'()+,.\/:;<=>\[\\\]_`{|}~]) // NEW

    return true;
}



/**
 * Get the dossier data to display when the Dossier name should be display
 * For example, it will add the DOS_CODE as prefix of the DOS_TITRE etc.
 * @author Mickaël "Naxe67" APPEL
 * @param {Dossier} dossier
 * @returns {string}
 */
export const getDossierNameFormated = (dossier :Dossier) => {
    return `${dossier.DOS_CODE} - ${dossier.DOS_TITRE}${(dossier.FIC_VAR && dossier.FIC_VAR.length > 0 && dossier.FIC_VAR[0]) ? ` (${dossier.FIC_VAR[0].libelle}: ${dossier.FIC_VAR[0].value})` : ''}`;
}